<template>
  <PageTemplate>

    <Loader v-if="!isLoaded"/>
    <div v-else>

      <BackButton
          :label="$t('general.button.back')"
          :to="{ name: 'userList' }"
          marginBottom="5"/>

      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          Qualifikationen - {{ userData?.firstName }} {{ userData?.lastName }} ({{
            userData?.engagementTypeTranslation
          }})
        </h6>

        <CreateButton :label="$t('user.create')" :to="{ name: 'userForm' }"/>
      </div>

      <div class="element-box">
        <UserMenu :userId="userId" :employeeType="employeeType"/>

        <div class="d-flex flex-wrap align-items-center justify-content-between mt-4" style="gap: 15px;">
          <ExcelExportButton
              :label="$t('general.excel_export')"
              :loading="isExcelLoaded"
              @click="getExcelExport"/>

          <button type="button" class="btn btn-outline-primary" @click="handleFormEducation(null)">
            Weiterbildungen hinzufügen
          </button>
        </div>

        <EmptyState
            v-if="!educations.length"
            icon="fa-graduation-cap"
            title="Keine Weiterbildungen gefunden"/>

        <div v-else class="table-responsive overflow-auto pt-4">
          <table class="table table-striped table-hover">
            <thead>
            <tr>
              <th>Bezeichnung</th>
              <th>Zeitraum</th>
              <th class="text-center">Art</th>
              <th class="text-center">Vereinbarung liegt vor</th>
              <th class="text-end pe-4"> UE-Anzahl</th>
              <th style="text-align: center;  width: 50px">{{ $t('general.actions') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="education in educations"
                :key="education.id"
                :class="['table-row', { 'bg-light-gray': education.note }]"
                @click="!isCoordinatorButNotHr && handleFormEducation(education.id)">

              <td>{{ education?.description }}</td>
              <td>{{ education?.startDate }} - {{ education?.endDate }}</td>

              <td class="text-center">
                {{ String(education?.type) === '1' ? 'intern' : 'extern' }}
              </td>
              <td class="text-center">
                {{ String(education?.agreementIsInPlace) === '1' ? 'ja' : 'nein' }}
              </td>
              <td class="text-end pe-4"> {{ education?.ueNumber }}</td>

              <td class="text-center">
                <button v-if="!isCoordinatorButNotHr"
                        class="btn btn-danger ms-2"
                        @click.stop="handleDeleteEducation(education?.id)">
                  <i class="os-icon os-icon-trash"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <Modal
        v-model="showModalForm"
        title="Weiterbildungen"
        :hide-cancel="true"
        :button-label="$t('general.button.save')"
        sizeModal="modal-xl"
        :hide-footer="true"
        classFooter="justify-content-center">
      <Form :userId="userId"
            :educationId="educationId"
            v-model:submitForm="submitForm"/>
    </Modal>

    <ModalDelete
        v-model="showModalDelete"
        :header="t('general.delete_item_header')"
        :onDelete="confirmDeleteEducation">
    </ModalDelete>
  </PageTemplate>
</template>


<script setup>
import userService from '@/api/User';
import CreateButton from '@/components/CreateButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import Modal from '@/components/modal/Modal.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import {downloadFile} from '@/shared/functions';
import Form from '@/views/App/User/FurtherEducation/Form.vue';
import UserMenu from '@/views/App/User/UserMenu.vue';
import {onMounted, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';
import ExcelExportButton from "@/components/ExcelExportButton.vue";
import BackButton from "@/components/button/BackButton.vue";

const route = useRoute();
const userId = route.params.id;
const {t} = useI18n();
const userData = ref(null);
const educations = ref([]);
const educationId = ref(null);
const showModalForm = ref(false);
const showModalDelete = ref(false);
const isLoaded = ref(false);
const submitForm = ref(false);
const isCoordinatorButNotHr = ref(false);
const employeeType = ref("")
const isExcelLoaded = ref(false);

watch(submitForm, () => {
  educationId.value = null;
  showModalForm.value = false;
  showModalDelete.value = false;
  educationId.value = null;
  getEducations();
});

async function getEducations(page = 1, perPage, passedFilters) {
  CloseToast();
  try {
    const {data} = await userService.getFurtherEducations(+userId);

    educations.value = data.list;
    isCoordinatorButNotHr.value = data.isCoordinatorButNotHr;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getUser() {
  try {
    const {data} = await userService.getUser(+userId);
    userData.value = data?.data?.user;
    employeeType.value = data?.data?.user?.employeeType

  } catch (error) {
    await ErrorToast(error);
  }
}

function handleDeleteEducation(id) {
  CloseToast();
  showModalForm.value = false;
  CloseToast();
  educationId.value = id;
  showModalDelete.value = true;
}

async function confirmDeleteEducation() {
  try {
    await userService.deleteFurtherEducation(educationId.value);
    showModalForm.value = false;
    showModalDelete.value = false;
    await getEducations();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

function handleFormEducation(id) {
  CloseToast();
  showModalForm.value = false;
  CloseToast();
  educationId.value = id;
  showModalDelete.value = false;
  showModalForm.value = true;
}

async function getExcelExport() {
  await downloadFile(`/user/further-education-excel-export/${userId}`, undefined,
      null,
      false,
      "GET",
      isExcelLoaded
  )
}


onMounted(async () => {
  await Promise.all([
    await getEducations(),
    await getUser(),
  ]);

  isLoaded.value = true;
});

</script>

<style scoped>

</style>